import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAppDispatch } from 'app/hooks';
import { setSnowflakeAccountId, completeStep } from '../reducers/onboardingSlice';
import { getSnowflakeErrorMessage } from '../services/snowflakeApi';
import Input from 'components/Input';
import { classNames } from 'utils/styleUtils';
import { UsernameSetupHelp } from './UsernameSetupHelp';
import { WarehouseSetupHelp } from './WarehouseSetupHelp';
import { RoleSetupHelp } from './RoleSetupHelp';
import { AccountValidationInput } from './AccountValidationInput';
import { AccountIdentifierSetupHelp } from './AccountIdentifierSetupHelp';
import { EMPTY_FIELD_VALUE } from 'utils/formDefaults';

/**
 * BYOKForm Component
 *
 * Form for Bring Your Own Key (BYOK) Snowflake authentication where users
 * provide their own private key for authentication.
 *
 * @component
 * @example
 * ```tsx
 * <BYOKForm />
 * ```
 */
const BYOKForm: React.FC = () => {
  const dispatch = useAppDispatch();

  // Form state
  const [accountIdentifier, setAccountIdentifier] = useState<string>(EMPTY_FIELD_VALUE);
  const [username, setUsername] = useState<string>('DATAOPS_ADMIN_USER');
  const [privateKey, setPrivateKey] = useState<string>(EMPTY_FIELD_VALUE);
  const [passphrase, setPassphrase] = useState<string>(EMPTY_FIELD_VALUE);
  const [warehouse, setWarehouse] = useState<string>('DATAOPS_ADMIN_WAREHOUSE');
  const [role, setRole] = useState<string>('DATAOPS_ADMIN_ROLE');

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Validates form fields
   */
  const isFormValid = (): boolean => {
    return (
      accountIdentifier.trim() !== '' &&
      username.trim() !== '' &&
      privateKey.trim() !== '' &&
      warehouse.trim() !== '' &&
      role.trim() !== ''
    );
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    const data = {
      type: 'pkey',
      account: accountIdentifier,
      username,
      private_key: privateKey,
      private_key_pwd: passphrase !== '' ? passphrase : null,
      warehouse,
      role,
    };

    try {
      const response = await axios.post<number>('/api/v1/snowflake/account', data);
      const accountId = response.data;

      // Store the Snowflake account ID for runner provisioning
      dispatch(setSnowflakeAccountId(accountId));

      setLoading(false);

      // Complete the step after a brief delay
      setTimeout(() => {
        dispatch(completeStep('snowflake_setup'));
      }, 1000);
    } catch (err: unknown) {
      console.error('BYOK account creation failed:', err);
      setLoading(false);
      const errorMessage = getSnowflakeErrorMessage(err);
      setError(
        errorMessage !== 'An unexpected error occurred' && errorMessage !== 'An error occurred'
          ? errorMessage
          : "Couldn't connect to Snowflake. Please check your credentials and try again.",
      );
    }
  };

  return (
    <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div>
        <h3 className="text-lg  text-dataops-dark">Bring Your Own Key</h3>
        <p className="mt-2 text-sm text-secondary-text">
          Use a private key you have generated externally for authentication.
        </p>
      </div>

      {error !== null && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md bg-red-50 border border-red-200 p-4"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex">
            <div className="ml-3">
              <h4 className="text-sm font-medium text-red-800">Connection failed</h4>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-5"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.3 }}
        >
          <AccountValidationInput
            value={accountIdentifier}
            onChange={setAccountIdentifier}
            disabled={loading}
            required
          />
          <AccountIdentifierSetupHelp />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <label htmlFor="role" className="block text-sm font-medium text-primary-text">
            Role{' '}
            <span className="text-red-highlight" aria-label="required">
              *
            </span>
          </label>
          <p className="mt-1 text-xs text-secondary-text">
            A Snowflake role with required account privileges. Use the help to create and grant it.
          </p>
          <div className="mt-2">
            <Input
              id="role"
              name="role"
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="DATAOPS_ADMIN_ROLE"
              required
              disabled={loading}
              aria-required="true"
            />
          </div>
          <RoleSetupHelp roleName={role} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.3 }}
        >
          <label htmlFor="warehouse" className="block text-sm font-medium text-primary-text">
            Warehouse{' '}
            <span className="text-red-highlight" aria-label="required">
              *
            </span>
          </label>
          <p className="mt-1 text-xs text-secondary-text">
            Create or select a Snowflake warehouse for DataOps tasks. Use the help to create one with recommended
            settings.
          </p>
          <div className="mt-2">
            <Input
              id="warehouse"
              name="warehouse"
              type="text"
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              placeholder="DATAOPS_ADMIN_WAREHOUSE"
              required
              disabled={loading}
              aria-required="true"
            />
          </div>
          <WarehouseSetupHelp warehouseName={warehouse} roleName={role} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <label htmlFor="username" className="block text-sm font-medium text-primary-text">
            User{' '}
            <span className="text-red-highlight" aria-label="required">
              *
            </span>
          </label>
          <p className="mt-1 text-xs text-secondary-text">
            The Snowflake user for authentication. Use the help to create a dedicated service user.
          </p>
          <div className="mt-2">
            <Input
              id="username"
              name="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="DATAOPS_ADMIN_USER"
              required
              disabled={loading}
              aria-required="true"
            />
          </div>
          <UsernameSetupHelp username={username} roleName={role} warehouseName={warehouse} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.3 }}
        >
          <label htmlFor="privateKey" className="block text-sm font-medium text-primary-text">
            Private Key{' '}
            <span className="text-red-highlight" aria-label="required">
              *
            </span>
          </label>
          <div className="mt-2">
            <textarea
              id="privateKey"
              name="privateKey"
              data-testid="input-privateKey"
              rows={4}
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----&#10;Paste your private key here&#10;-----END PRIVATE KEY-----"
              required
              disabled={loading}
              aria-required="true"
              aria-describedby="privateKey-help"
              className={classNames(
                privateKey === '' ? '' : 'blurred-text',
                'block w-full rounded-md bg-white px-3 py-2 text-base text-primary-text outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-dataops-primary-blue disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed sm:text-sm resize-y',
              )}
            />
          </div>
          <p id="privateKey-help" className="mt-2 text-xs text-secondary-text">
            The private key will be blurred for security
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <label htmlFor="passphrase" className="block text-sm font-medium text-primary-text">
            Passphrase <span className="text-secondary-text text-xs">(optional)</span>
          </label>
          <div className="mt-2">
            <Input
              id="passphrase"
              name="passphrase"
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Passphrase for private key"
              disabled={loading}
              aria-describedby="passphrase-help"
            />
          </div>
          <p id="passphrase-help" className="mt-2 text-xs text-secondary-text">
            Only required if your private key is encrypted with a passphrase
          </p>
        </motion.div>

        <div className="flex justify-end pt-6">
          <motion.button
            type="submit"
            disabled={loading || !isFormValid()}
            className="inline-flex justify-center py-3 px-8 border border-transparent shadow-sm text-base  rounded-md text-white bg-dataops-primary-blue hover:bg-hover-primary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
            whileHover={!loading && isFormValid() ? { scale: 1.02 } : {}}
            whileTap={!loading && isFormValid() ? { scale: 0.98 } : {}}
            aria-label={loading ? 'Connecting to Snowflake' : 'Connect Snowflake account'}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                  aria-hidden="true"
                />
                Connecting...
              </span>
            ) : (
              'Connect Snowflake Account'
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default BYOKForm;
